/**
 * <valco-store-locator>
 *
 * Server-rendered list of dealer cards is the no-JS baseline. This component
 * upgrades it with: Leaflet map (lazy-loaded), search, type-filter chips,
 * "near me" geolocation, list↔map sync, and a mobile list/map tab toggle.
 *
 * Reads its dealer payload from a child <script type="application/json"
 * data-dealers> emitted by the Liquid section. All UI strings come from
 * the host theme via Liquid (no in-component i18n).
 */

(function () {
  if (customElements.get('valco-store-locator')) return;

  const SCRIPT_LOAD_CACHE = new Map();
  function loadScriptOnce(url) {
    if (SCRIPT_LOAD_CACHE.has(url)) return SCRIPT_LOAD_CACHE.get(url);
    const p = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = url;
      s.async = true;
      s.onload = resolve;
      s.onerror = () => reject(new Error('Failed to load ' + url));
      document.head.appendChild(s);
    });
    SCRIPT_LOAD_CACHE.set(url, p);
    return p;
  }

  function haversineKm(a, b) {
    const R = 6371;
    const toRad = (x) => (x * Math.PI) / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(h));
  }

  function formatDistance(km) {
    if (!isFinite(km)) return '';
    if (km < 1) return Math.round(km * 1000) + ' m';
    if (km < 10) return km.toFixed(1) + ' km';
    return Math.round(km) + ' km';
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  class ValcoStoreLocator extends HTMLElement {
    constructor() {
      super();
      this._dealers = [];
      this._activeId = null;
      this._activeTypes = new Set();
      this._fittingActive = false;
      this._searchTerm = '';
      this._userLatLng = null;
      this._map = null;
      this._cluster = null;
      this._userMarker = null;
      this._markersById = new Map();
      this._cardsById = new Map();
      this._countNode = null;
      this._emptyNode = null;
      this._countTemplate = '';
      this._initializing = false;
    }

    connectedCallback() {
      if (this._initializing) return;
      this._initializing = true;
      this._initialize().catch((err) => {
        console.error('[valco-store-locator] initialization failed:', err);
        this._handleMapUnavailable();
      });
    }

    async _initialize() {
      const dataEl = this.querySelector('script[data-dealers]');
      if (!dataEl) return;
      try {
        this._dealers = JSON.parse(dataEl.textContent || '[]');
      } catch (e) {
        return;
      }

      await this._loadRemainingPages();

      this._countNode = this.querySelector('[data-count]');
      this._emptyNode = this.querySelector('[data-empty]');
      this._countTemplate = this._countNode ? this._countNode.textContent.trim() : '';

      this.querySelectorAll('.valco-sl__card').forEach((card) => {
        const id = Number(card.dataset.dealerId);
        this._cardsById.set(id, card);
      });

      this._sortDealersAndCards();

      this._bindEvents();
      this._renderTypeChips();
      this._updateCount(this._dealers.length);
      this._applyMobileView(this.dataset.defaultMobileView || 'list');

      if (window.matchMedia('(prefers-reduced-data: reduce)').matches && !this.hasAttribute('data-force-map')) {
        // Leave the no-JS list rendered; skip loading the map bundle.
        this._handleMapUnavailable();
        return;
      }

      // Lazy-load Leaflet only when the section is near the viewport.
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries, obs) => {
          if (entries.some((e) => e.isIntersecting)) {
            obs.disconnect();
            this._bootMap();
          }
        }, { rootMargin: '200px' });
        io.observe(this);
      } else {
        this._bootMap();
      }
    }

    async _loadRemainingPages() {
      const totalPages = Number(this.dataset.paginationPages) || 1;
      const currentPage = Number(this.dataset.paginationPage) || 1;
      const pageParam = this.dataset.paginationParam || 'page';
      const sectionId = this.dataset.sectionId;
      const list = this.querySelector('[data-list]');
      if (totalPages <= 1 || !sectionId || !list) {
        this.dataset.paginationState = 'complete';
        return;
      }

      const dealerIds = new Set(this._dealers.map((dealer) => Number(dealer.id)));
      const cardIds = new Set(
        Array.from(list.querySelectorAll('.valco-sl__card')).map((card) => Number(card.dataset.dealerId))
      );
      let failed = false;

      for (let page = 1; page <= totalPages; page++) {
        if (page === currentPage) continue;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('section_id', sectionId);
          url.searchParams.set(pageParam, String(page));
          const response = await fetch(url.toString(), {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          });
          if (!response.ok) throw new Error('HTTP ' + response.status);

          const documentPage = new DOMParser().parseFromString(await response.text(), 'text/html');
          const sectionPage = documentPage.querySelector('valco-store-locator');
          const dataPage = sectionPage && sectionPage.querySelector('script[data-dealers]');
          if (!sectionPage || !dataPage) throw new Error('Missing paginated section markup');

          const dealersPage = JSON.parse(dataPage.textContent || '[]');
          for (const dealer of dealersPage) {
            const id = Number(dealer.id);
            if (dealerIds.has(id)) continue;
            dealerIds.add(id);
            this._dealers.push(dealer);
          }

          sectionPage.querySelectorAll('[data-list] > .valco-sl__card').forEach((card) => {
            const id = Number(card.dataset.dealerId);
            if (cardIds.has(id)) return;
            cardIds.add(id);
            list.appendChild(document.importNode(card, true));
          });
        } catch (err) {
          failed = true;
          console.error('[valco-store-locator] dealer page ' + page + ' failed:', err);
        }
      }

      this.dataset.paginationState = failed ? 'partial' : 'complete';
      this.dataset.paginationLoaded = String(this._dealers.length);
    }

    _sortDealersAndCards() {
      const list = this.querySelector('[data-list]');
      if (!list) return;
      const locale = document.documentElement.lang || undefined;
      this._dealers.sort((a, b) => {
        const featuredOrder = Number(Boolean(b.featured)) - Number(Boolean(a.featured));
        if (featuredOrder !== 0) return featuredOrder;
        return String(a.name || '').localeCompare(String(b.name || ''), locale, {
          numeric: true,
          sensitivity: 'base',
        });
      });
      for (const dealer of this._dealers) {
        const card = this._cardsById.get(Number(dealer.id));
        if (card) list.appendChild(card);
      }
    }

    _bootMap() {
      this._loadLeaflet()
        .then(() => this._initMap())
        .catch((err) => {
          console.error('[valco-store-locator] map init failed:', err);
          this._handleMapUnavailable();
        });
    }

    _handleMapUnavailable() {
      // Map can't render — hide the map pane AND the list/map tab toggle so
      // the user can't end up on an empty "Map" view on mobile. Force list.
      const mapPane = this.querySelector('.valco-sl__pane--map');
      if (mapPane) mapPane.hidden = true;
      const tabs = this.querySelector('.valco-sl__tabs');
      if (tabs) tabs.hidden = true;
      this._applyMobileView('list');
    }

    /* ── Leaflet bootstrap ─────────────────────────────────────── */

    _loadLeaflet() {
      const leafletJs = this.dataset.leafletJs;
      const clusterJs = this.dataset.clusterJs;
      if (!leafletJs || !clusterJs) return Promise.reject(new Error('Missing Leaflet asset URLs'));
      return loadScriptOnce(leafletJs)
        .then(() => loadScriptOnce(clusterJs))
        .then(() => {
          if (!window.L || !window.L.markerClusterGroup) {
            throw new Error('Leaflet did not load');
          }
        });
    }

    _initMap() {
      const L = window.L;
      const mapEl = this.querySelector('[data-map]');
      if (!mapEl) return;

      this._map = L.map(mapEl, {
        scrollWheelZoom: false,
        zoomControl: true,
      }).setView(
        [Number(this.dataset.defaultLat) || 62.5, Number(this.dataset.defaultLng) || 26.0],
        Number(this.dataset.defaultZoom) || 5
      );

      L.tileLayer(this.dataset.tileUrl, {
        attribution: this.dataset.tileAttrib || '',
        maxZoom: 18,
      }).addTo(this._map);

      this._cluster = L.markerClusterGroup({
        showCoverageOnHover: false,
        spiderfyOnMaxZoom: true,
      });

      const pinColor = this.dataset.pinColor || '#171717';
      const icon = this._buildPinIcon(pinColor);

      for (const d of this._dealers) {
        // Skip online-only dealers — no physical location on the map.
        if (d.online) continue;
        const lat = Number(d.lat);
        const lng = Number(d.lng);
        if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) continue;
        const m = L.marker([lat, lng], { title: d.name, icon }).bindPopup(this._popupHtml(d));
        m.on('click', () => this._selectDealer(d.id, { fromMap: true }));
        this._markersById.set(d.id, m);
        this._cluster.addLayer(m);
      }

      this._map.addLayer(this._cluster);

      const autoFit = this.dataset.autoFit !== '0';
      if (autoFit) {
        try {
          const bounds = this._cluster.getBounds();
          if (bounds.isValid()) this._map.fitBounds(bounds, { padding: [40, 40], maxZoom: 11 });
        } catch (_) {
          /* noop */
        }
      }
    }

    _buildPinIcon(color) {
      const L = window.L;
      const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
        <path d="M14 0C6.27 0 0 6.27 0 14c0 9.6 12.5 25 13.04 25.65a1.25 1.25 0 0 0 1.92 0C15.5 39 28 23.6 28 14c0-7.73-6.27-14-14-14Z" fill="${color}"/>
        <circle cx="14" cy="14" r="5.2" fill="#fff"/>
      </svg>`;
      return L.divIcon({
        className: 'valco-sl__pin',
        html: svg,
        iconSize: [28, 40],
        iconAnchor: [14, 40],
        popupAnchor: [0, -36],
      });
    }

    _popupHtml(d) {
      const parts = [];
      parts.push('<strong>' + escapeHtml(d.name) + '</strong>');
      const line = [];
      if (d.address) line.push(escapeHtml(d.address));
      const cityLine = [d.postal, d.city].filter(Boolean).map(escapeHtml).join(' ');
      if (cityLine) line.push(cityLine);
      if (d.country && d.country !== 'Finland') line.push(escapeHtml(d.country));
      if (line.length) parts.push(line.join('<br>'));
      if (d.url) {
        const urlStr = String(d.url);
        const display = urlStr.replace(/^https?:\/\//, '').replace(/\/$/, '');
        parts.push('<a href="' + escapeHtml(urlStr) + '" target="_blank" rel="noopener nofollow">' + escapeHtml(display) + '</a>');
      }
      return parts.join('<br>');
    }

    /* ── Event wiring ──────────────────────────────────────────── */

    _bindEvents() {
      const search = this.querySelector('[data-search]');
      if (search) {
        search.addEventListener('input', (e) => {
          this._searchTerm = (e.target.value || '').trim().toLowerCase();
          this._applyFilters();
        });
      }

      const nearMe = this.querySelector('[data-near-me]');
      if (nearMe) nearMe.addEventListener('click', () => this._handleNearMe());

      this.querySelectorAll('[data-tab]').forEach((btn) => {
        btn.addEventListener('click', () => this._applyMobileView(btn.dataset.tab));
      });

      this.addEventListener('click', (e) => {
        const showBtn = e.target.closest('[data-show-on-map]');
        if (showBtn && this.contains(showBtn)) {
          e.preventDefault();
          e.stopPropagation();
          const id = Number(showBtn.dataset.showOnMap);
          this._selectDealer(id, { fromList: true });
          if (window.matchMedia('(max-width: 1023px)').matches) {
            this._applyMobileView('map');
          }
          return;
        }
        const card = e.target.closest('.valco-sl__card');
        if (!card || !this.contains(card)) return;
        // Ignore clicks on inner links/buttons (phone, website, email, etc.)
        if (e.target.closest('a, button')) return;
        const id = Number(card.dataset.dealerId);
        this._selectDealer(id, { fromList: true });
      });

      this.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const card = e.target.closest && e.target.closest('.valco-sl__card');
        if (!card || !this.contains(card)) return;
        if (e.target.tagName === 'A') return;
        e.preventDefault();
        const id = Number(card.dataset.dealerId);
        this._selectDealer(id, { fromList: true });
      });
    }

    /* ── Type-filter chips ─────────────────────────────────────── */

    _renderTypeChips() {
      const container = this.querySelector('[data-type-chips]');
      if (!container) return;

      const counts = new Map();
      for (const d of this._dealers) {
        const types = Array.isArray(d.types) ? d.types : [];
        for (const t of types) {
          if (!t) continue;
          counts.set(t, (counts.get(t) || 0) + 1);
        }
      }
      const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));

      const ICON_GLOBE = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>';
      const ICON_STORE = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M3 9l1-5h16l1 5"/><path d="M5 9v11h14V9"/><path d="M3 9a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0"/></svg>';
      const ICON_HEADPHONES = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 14h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a9 9 0 0 1 18 0v7a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3"/></svg>';

      container.innerHTML = '';
      for (const [type] of sorted) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'valco-sl__chip';
        btn.dataset.type = type;
        btn.setAttribute('aria-pressed', 'false');
        btn.title = type;
        btn.setAttribute('aria-label', type);
        const lower = type.toLowerCase();
        if (lower.indexOf('verkko') !== -1) {
          btn.innerHTML = ICON_GLOBE;
        } else if (lower.indexOf('myym') !== -1) {
          btn.innerHTML = ICON_STORE;
        } else {
          btn.textContent = type;
        }
        btn.addEventListener('click', () => {
          if (this._activeTypes.has(type)) {
            this._activeTypes.delete(type);
            btn.setAttribute('aria-pressed', 'false');
          } else {
            this._activeTypes.add(type);
            btn.setAttribute('aria-pressed', 'true');
          }
          this._applyFilters();
        });
        container.appendChild(btn);
      }

      // Fitting-availability chip — separate filter dimension. Only render it
      // when at least one dealer actually offers try-on, so the control stays
      // hidden until that data is collected.
      if (this._dealers.some((d) => d.fitting === true)) {
        const label = this.dataset.fittingLabel || '';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'valco-sl__chip valco-sl__chip--fitting';
        btn.dataset.fitting = '1';
        btn.setAttribute('aria-pressed', 'false');
        if (label) {
          btn.title = label;
          btn.setAttribute('aria-label', label);
        }
        btn.innerHTML = ICON_HEADPHONES;
        btn.addEventListener('click', () => {
          this._fittingActive = !this._fittingActive;
          btn.setAttribute('aria-pressed', this._fittingActive ? 'true' : 'false');
          this._applyFilters();
        });
        container.appendChild(btn);
      }
    }

    /* ── Filtering ─────────────────────────────────────────────── */

    _applyFilters() {
      const term = this._searchTerm;
      const types = this._activeTypes;
      let visible = 0;

      for (const d of this._dealers) {
        const card = this._cardsById.get(d.id);
        if (!card) continue;
        const hay = (
          (d.name || '') + ' ' +
          (d.city || '') + ' ' +
          (d.postal || '') + ' ' +
          (d.address || '') + ' ' +
          ((d.types || []).join(' '))
        ).toLowerCase();

        const matchesSearch = term === '' || hay.indexOf(term) !== -1;
        // AND-logic: when multiple chips are active, dealer must have ALL of them.
        const dealerTypes = Array.isArray(d.types) ? d.types : [];
        let matchesType = true;
        if (types.size > 0) {
          for (const t of types) {
            if (!dealerTypes.includes(t)) { matchesType = false; break; }
          }
        }
        const matchesFitting = !this._fittingActive || d.fitting === true;
        const show = matchesSearch && matchesType && matchesFitting;

        card.classList.toggle('is-hidden', !show);
        if (show) visible++;

        const m = this._markersById.get(d.id);
        if (m && this._cluster) {
          const has = this._cluster.hasLayer(m);
          if (show && !has) this._cluster.addLayer(m);
          else if (!show && has) this._cluster.removeLayer(m);
        }
      }

      this._updateCount(visible);
      if (this._emptyNode) this._emptyNode.hidden = visible !== 0;
    }

    _updateCount(n) {
      if (!this._countNode || !this._countTemplate) return;
      // Locale string is e.g. "{{ count }} jälleenmyyjää" — Liquid pre-renders the
      // initial count, so we restore that for the all-dealers case and replace
      // with a digit otherwise. A simple regex swap is enough for our keys.
      const replaced = this._countTemplate.replace(/\d+/, String(n));
      this._countNode.textContent = replaced;
    }

    /* ── Selection / list↔map sync ─────────────────────────────── */

    _selectDealer(id, opts) {
      opts = opts || {};
      if (this._activeId === id && !opts.fromMap && !opts.fromList) return;

      const prevCard = this._activeId != null ? this._cardsById.get(this._activeId) : null;
      if (prevCard) {
        prevCard.classList.remove('is-active');
        prevCard.setAttribute('aria-pressed', 'false');
      }

      this._activeId = id;
      const card = this._cardsById.get(id);
      const marker = this._markersById.get(id);

      if (card) {
        card.classList.add('is-active');
        card.setAttribute('aria-pressed', 'true');
        if (opts.fromMap) {
          card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }

      if (marker && this._map) {
        if (opts.fromList) {
          if (this._cluster && typeof this._cluster.zoomToShowLayer === 'function') {
            this._cluster.zoomToShowLayer(marker, () => marker.openPopup());
          } else {
            this._map.flyTo(marker.getLatLng(), Math.max(this._map.getZoom(), 12), { duration: 0.6 });
            marker.openPopup();
          }
          // On mobile, switch to the map view so the user sees the result.
          if (window.matchMedia('(max-width: 1023px)').matches) {
            this._applyMobileView('map');
          }
        }
      }
    }

    /* ── Near me ───────────────────────────────────────────────── */

    _handleNearMe() {
      if (!('geolocation' in navigator)) return;
      const btn = this.querySelector('[data-near-me]');
      if (btn) btn.disabled = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          if (btn) btn.disabled = false;
          const ll = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          if (!isFinite(ll.lat) || !isFinite(ll.lng)) return;
          this._userLatLng = ll;
          this._sortByDistanceFrom(ll);
          if (this._map) {
            this._map.flyTo([ll.lat, ll.lng], 9, { duration: 0.7 });
            this._addUserMarker(ll);
          }
          if (window.matchMedia('(max-width: 1023px)').matches) {
            this._applyMobileView('map');
          }
        },
        (err) => {
          if (btn) btn.disabled = false;
          console.warn('[valco-store-locator] geolocation failed:', err && err.message);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    }

    _sortByDistanceFrom(ll) {
      const list = this.querySelector('[data-list]');
      if (!list) return;

      const items = Array.from(list.children);
      const decorated = items.map((card) => {
        const lat = Number(card.dataset.lat);
        const lng = Number(card.dataset.lng);
        const km = isFinite(lat) && isFinite(lng) && !(lat === 0 && lng === 0)
          ? haversineKm(ll, { lat, lng })
          : Infinity;
        return { card, km };
      });
      decorated.sort((a, b) => a.km - b.km);

      const frag = document.createDocumentFragment();
      for (const { card, km } of decorated) {
        const dist = card.querySelector('[data-distance]');
        if (dist) {
          if (isFinite(km)) {
            dist.textContent = formatDistance(km);
            dist.hidden = false;
          } else {
            dist.hidden = true;
          }
        }
        frag.appendChild(card);
      }
      list.appendChild(frag);
    }

    _addUserMarker(ll) {
      const L = window.L;
      if (!L) return;
      if (this._userMarker) {
        this._userMarker.setLatLng([ll.lat, ll.lng]);
        return;
      }
      const icon = L.divIcon({
        className: '',
        html: '<div class="valco-sl__user-marker" aria-hidden="true"></div>',
        iconSize: [16, 16],
        iconAnchor: [8, 8],
      });
      this._userMarker = L.marker([ll.lat, ll.lng], { icon, interactive: false }).addTo(this._map);
    }

    /* ── Mobile list/map tabs ──────────────────────────────────── */

    _applyMobileView(view) {
      this.classList.toggle('is-list', view === 'list');
      this.classList.toggle('is-map', view === 'map');
      this.querySelectorAll('[data-tab]').forEach((b) => {
        b.setAttribute('aria-selected', b.dataset.tab === view ? 'true' : 'false');
      });
      // Leaflet needs its size invalidated when the map pane becomes visible
      // after being display:none on mobile.
      if (view === 'map' && this._map) {
        requestAnimationFrame(() => {
          try { this._map.invalidateSize(); } catch (_) { /* noop */ }
        });
      }
    }
  }

  customElements.define('valco-store-locator', ValcoStoreLocator);
})();







